import * as Sentry from "@sentry/react";
import axios from "axios";
import type { Folder } from "@theirstory/common/api_types/backend";

import type { ActionTypeHelper } from "../state/rootStore";

import { notifySuccess } from "./notify";
import { buildFilterQueryParams } from "./filterQueryParams";
import type { ProjectFilterFacetsResponse, ProjectStoriesFilters } from "./projects";
import {
  FOLDERS_LOADED,
  FOLDER_DETAILS_LOADED,
  FOLDER_STORIES_LOADED,
  FOLDER_STORIES_LOADING,
  FOLDER_STORIES_SET_PAGE_VIEW,
  FOLDER_STORIES_SORT_SETTING,
  FOLDER_STORIES_FILTER_SETTING,
  FOLDERS_RESET_CURRENT,
  FOLDERS_LOADING,
  FOLDERS_TOGGLE_CREATE_FOLDER_MODAL,
  RESET_FOLDER_STATE,
  RESET_FOLDER_STORIES,
} from "./types";

export const getProjectFolders =
  (projectId: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    try {
      dispatch({ type: FOLDERS_LOADING });
      const { data } = await axios.get(`/projects/${projectId}/folders`);
      dispatch({ type: FOLDERS_LOADED, payload: data });
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  };

export const getFolder =
  (folderId: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    dispatch(loadFolderDetails(folderId));
    dispatch(loadFolderStories({ folderId }));
  };

export const loadFolderDetails = (folderId: string) => async (dispatch) => {
  try {
    const { data } = await axios.get(`/folders/${folderId}`);
    dispatch({ type: FOLDER_DETAILS_LOADED, payload: { folderDetails: data } });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const loadFolderFilterFacets =
  (folderId: string): ActionTypeHelper<Promise<ProjectFilterFacetsResponse>> =>
  async () => {
    try {
      const { data } = await axios.get<ProjectFilterFacetsResponse>(`/folders/${folderId}/filter-facets`);
      return data;
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "sessions_table_filters");
        scope.setTag("method", "loadFolderFilterFacets");
        scope.setTag("folderId", folderId);
        Sentry.captureException(error);
      });
      throw error;
    }
  };

export const loadFolderStories =
  (params: { folderId: string; filters?: ProjectStoriesFilters | null; page?: number }) =>
  async (dispatch, getState) => {
    try {
      const {
        folders: {
          folder: {
            paging: { size: pageSize, current: currentPage },
            sort: { column, order },
            filters: stateFilters,
          },
        },
      } = getState();

      const { folderId } = params;
      const activeFilters = params.filters !== undefined ? params.filters : stateFilters;
      const targetPage = params.page !== undefined ? params.page : params.filters !== undefined ? 1 : currentPage;

      // Loading indicator only shows on the first page; later pages append via infinite scroll.
      if (targetPage === 1) {
        dispatch({ type: FOLDER_STORIES_LOADING });
      }
      const urlPath = `/folders/${folderId}/stories`;
      const searchParam = new URLSearchParams(window.location.search).get("search");

      const res = await axios.get(urlPath, {
        params: {
          pageSize,
          page: targetPage,
          search: searchParam,
          sort: column,
          direction: order,
          ...buildFilterQueryParams(activeFilters),
        },
      });

      const { items: newStories, total } = res.data;
      // When filters change we reset and start at page 1, so previous stories must be discarded.
      // Dedupe by _id when merging later pages guards against repeats under an unstable sort.
      const existingStories = targetPage === 1 ? [] : getState().folders.folder.stories;
      const stories = [...existingStories, ...newStories].filter(
        (story, idx, arr) => arr.findIndex((s) => s._id === story._id) === idx
      );

      let page = targetPage;
      if (stories.length < total) {
        page = targetPage + 1;
      }

      dispatch({ type: FOLDER_STORIES_LOADED, payload: { stories, total, page, folderId } });
      return stories;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

export const setFolderStoriesPageView =
  ({ currentPage, pageSize }: any) =>
  async (dispatch) => {
    dispatch({ type: FOLDER_STORIES_SET_PAGE_VIEW, payload: { currentPage, pageSize } });
  };

export const setFolderStoriesSort = (column, order) => async (dispatch) => {
  dispatch({ type: FOLDER_STORIES_SORT_SETTING, payload: { column, order } });
};

export const setFolderStoriesFilters = (filters: ProjectStoriesFilters | null) => async (dispatch) => {
  dispatch({ type: FOLDER_STORIES_FILTER_SETTING, payload: { filters } });
};

export const loadFolderMetadataOptions =
  (folderId: string, columnId: string, fieldKey?: string): ActionTypeHelper<Promise<string[]>> =>
  async () => {
    try {
      const { data } = await axios.get<{ options: string[] }>(`/folders/${folderId}/metadata-options`, {
        params: { columnId, fieldKey },
      });
      return Array.isArray(data?.options) ? data.options : [];
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "sessions_table_filters");
        scope.setTag("method", "loadFolderMetadataOptions");
        scope.setTag("folderId", folderId);
        Sentry.captureException(error);
      });
      throw error;
    }
  };

export const editFolder = (projectId: string, folderId: string, values) => async (dispatch) => {
  try {
    const res = await axios.put(`/folders/${folderId}/edit`, values);
    dispatch(getProjectFolders(projectId));
    return res.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const deleteFolder = (projectId: string, folderId: string) => async (dispatch) => {
  try {
    dispatch({ type: FOLDERS_LOADING });
    const res = await axios.delete(`/folders/${folderId}`);
    dispatch(getProjectFolders(projectId));
    dispatch(notifySuccess(`Successfully deleted the folder`));
    return res.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const createFolder = (projectId: string, name: string) => async (dispatch) => {
  try {
    dispatch({ type: FOLDERS_LOADING });
    const res = await axios.post("/folders/create", {
      name,
      project: projectId,
    });
    dispatch(notifySuccess(`Successfully created the folder, ${res.data.name}`));
    dispatch(getProjectFolders(projectId));
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const resetCurrentFolder = () => async (dispatch) => {
  dispatch({ type: FOLDERS_RESET_CURRENT });
};

export const resetFolderState = () => async (dispatch) => {
  dispatch({ type: RESET_FOLDER_STATE });
};

export const resetFolderStories = () => async (dispatch) => {
  dispatch({ type: RESET_FOLDER_STORIES });
};

export const openCreateFolderModal = (isOpen) => async (dispatch) => {
  dispatch({ type: FOLDERS_TOGGLE_CREATE_FOLDER_MODAL, payload: { isOpen: isOpen } });
};

export type FoldersLoaded = Folder[];
